import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { forkJoin, Observable, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { AlertService } from './alert.service';
import { User } from '../models/user';
import { AppService } from './app.service';
import { CustomerService } from './customer.service';
import { DateTimeService } from './date-time.service';
import { ErrorService } from './error.service';
import { GlobalService } from './global.service';
import { OrderService } from './order.service';
import { PushService } from './push.service';
import { UserService } from './user.service';
import { WorkerService } from './worker.service';
import { ModalService } from './modal.service';
import { AppConfig } from '../app.config';

@Injectable()
export class AuthenticationService {
	public idleState$: EventEmitter<any>;
	public loggedIn$: EventEmitter<any>;
	public sessionExpiration: any;
	public showAuthModal$: EventEmitter<any>;
	public showTimeoutBanner$: EventEmitter<any>;
	public showWhatsNew$: EventEmitter<boolean>;
	public timerSubscription: any;
	private bannerCurrentlyShowing: boolean = false;
	public user = new User();
	public version: any;
  	public whatsNewVersion: string = '22.4.0.0';
	public sessionTimeoutMinutes: number = 20;

	constructor(private _http: HttpClient,
				private _userService: UserService,
				private _dateTimeService: DateTimeService,
				private _customerService: CustomerService,
				private _workerService: WorkerService,
				private _globalService: GlobalService,
				private _errorService: ErrorService,
				private _appService: AppService,
				private _orderService: OrderService,
				private _pushService: PushService,
				private _alertService: AlertService,
				private _modalService: ModalService,
				private _appConfig: AppConfig) {
		this.showTimeoutBanner$ = new EventEmitter();
		this.idleState$ = new EventEmitter<any>();
		this.loggedIn$ = new EventEmitter<any>();
		this.showAuthModal$ = new EventEmitter<boolean>();
		this.showWhatsNew$ = new EventEmitter<boolean>();
		this.version = this._appService.version;
		this.setupUserInteractionListeners();
	}

	set expirationOfSession(val) {
		this.sessionExpiration = val;
	}

	private setupUserInteractionListeners() {
		// Listen for user interactions to reset session and hide banner
		const events = ['click', 'keydown', 'input', 'change', 'focus'];
		
		events.forEach(eventType => {
			document.addEventListener(eventType, () => {
				this.onUserInteraction();
			}, true);
		});
	}

	private onUserInteraction() {
		// Reset session timeout when user interacts with page
		if (this.sessionExpiration && this.bannerCurrentlyShowing) {
			const sessionTimeoutMinutes = this.sessionTimeoutMinutes || 20;
			const newSessionExpiration = new Date();
			newSessionExpiration.setMinutes(newSessionExpiration.getMinutes() + sessionTimeoutMinutes);
			this.sessionExpiration = newSessionExpiration;
		}
	}

	authenticationDelete(reason?) {
		if (reason) {
			this.showAuthModal(reason);
		}
		this._userService.deleteUser();
		localStorage.removeItem('Rapidship:customer');
		this._appConfig.getAnonymousOptions();
		this.showTimeoutBanner$.emit(false);
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}
	}

	/**
	 * @api {post} /v2/Authentication/CaptchaVerification Captcha Verification
	 * @apiName captchaVerification
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	captchaVerification(token) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/CaptchaVerification`, JSON.stringify(token));
	}

	/**
	 * @api {post} /v2/Authentication/EmailExists Check If Email Already Exists
	 * @apiName emailExists
	 * @apiGroup Authorization
	 * @apiVersion 20.0.0
	 */
	emailExists(data) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authorization/EmailExists`, data);
	}

	initiateLogin(credentials, userType) {
		return new Observable((observer) => {
			this.login(credentials, userType).subscribe({
				next: (response: any) => {
					this.loginInitiationSuccess(credentials, userType, observer, response);
				}, error: (error) => {
					observer.error(error);
				}
			});
		});
	}

	loginInitiationSuccess(credentials, userType, observer, response) {
		this._orderService.clearOrderQueue();
		this.user = new User();
		this.user.type = userType;
		this.user.username = credentials.username;
		this.user.token = response.token ? response.token : response;
		this._userService.updateUser(this.user);
		// if rememberMe is checked save username to localStorage plus the userType else if unchecked and is set then remove it.
		if (credentials.rememberMe) {
			localStorage.setItem('Rapidship:rememberMe', JSON.stringify({
				username: credentials.username,
				userType: this.user.type
			}));
		} else if (localStorage.getItem('Rapidship:rememberMe')) {
			localStorage.removeItem('Rapidship:rememberMe');
		}

		if (this.user.type === 'inet') {
			if (response.profile) {
				Object.assign(this.user, this.user, response.profile);
				this._userService.updateUser(this.user);
				this.loginInet(this.user, observer);
			} else {
				observer.error({message: 'Did not retrieve necessary profile data, unable to log you in!'});
			}
		} else {
			this.loginWorker(this.user, observer);
		}
	}

	login(loginCreds, type) {
		return new Observable((observer) => {
			if (type === 'inet') {
				this.loginInetUser(loginCreds).subscribe({
					next: (response) => {
						observer.next(response);
						observer.complete();
					}, error: (error) => {
						observer.error(error);
					}
				});
			} else {
				this.loginHRUser(loginCreds).subscribe({
					next: (response) => {
						observer.next(response);
						observer.complete();
					}, error: (error) => {
						observer.error(error);
					}
				});
			}
		});
	}

	/**
	 * @api {post} /v2/Authentication/Worker Login HR User
	 * @apiName loginHRUser
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	loginHRUser(loginCreds) {
		const workerCreds = {
			username: loginCreds.username,
			password: loginCreds.password
		};
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/Worker`, JSON.stringify(workerCreds));
	}

	loginInet(user, observer) {
		this._globalService.getGlobalOptions().subscribe({
			next: (response) => {
				user.globalSettings = {...user.globalSettings, ...response};
				this._dateTimeService.processDateTimeFormat(response.dateFormat, response.timeDisplayFormat);
				const obs = [this._customerService.getAllowedCustomers()];
				if (user.inetUser.canViewShippingOptions) {
					obs.push(this._orderService.getAvailableShippingLabels());
				}
				forkJoin(obs).subscribe({
					next: ([allowedCustomers, shippingLabels]) => {
						// There is a permission that can be true to use shipping labels but there can be
						// no shipping labels to use so set permission to false if no labels so options are appropriately hidden
						// same applies to printShippingLabel which will auto print shipping labels even if no labels exist :/
						if (shippingLabels?.length === 0) {
							user.inetUser.canViewShippingOptions = false;
							user.inetUser.printShippingLabel = false;
						}
						Object.assign(user, user, {allowedCustomers: allowedCustomers});
						const selectedCustomer = user.allowedCustomers.filter((customer) => customer.id === user.primaryCustomerId)[0];
						forkJoin([
							this._customerService.getCustomer(selectedCustomer.id),
							this._globalService.getLicenses(),
							this._userService.getInetUserMiscData()
						])
							.subscribe({
								next: ([customer, licenses, miscData]) => {
									this._customerService.updateSelectedCustomer(customer);
									Object.assign(user, user, {licenses: licenses});
									user.inetUser.miscData = miscData;
									// Check if inet user has saved misc and if the applicationVersion matches current version and if current matches whatsNewVersion
									if (user.inetUser.miscData === null || (user.inetUser.miscData && user.inetUser.miscData.applicationVersion !== this.version && user.inetUser.miscData.applicationVersion < this.whatsNewVersion)) {
										this.showWhatsNew$.emit(true);
										if (!user.inetUser.miscData) {
											user.inetUser.miscData = {};
										}
										user.inetUser.miscData.applicationVersion = this.version;
										this._userService.setInetUserMiscData(user.inetUser.miscData).subscribe({
											next: () => {

											}, error: (error) => {
												this._errorService.error(error, 'set setInetUserMiscData failed');
											}
										});
									}
									this._userService.updateUser(user);
									this._pushService.registerForPush();
									observer.next('place-order');
									observer.complete();
								}, error: (error) => {
									console.warn(error);
									observer.error(error);
								}
							});
					}, error: (error) => {
						console.warn(error);
						observer.error(error);
					}
				});
			}, error: (error) => {
				observer.error(error);
			}
		});

		this.loggedIn$.emit(user.type);
	}

	/**
	 * @api {post} /v2/Authentication/InetUser?retrieveProfile=true Login Inet User
	 * @apiName loginInetUser
	 * @apiParam {boolean} [retrieveProfile] Retrieve Profile
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	loginInetUser(loginCreds) {
		let params: HttpParams = new HttpParams();
		params = params.append('retrieveProfile', 'true');
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/InetUser`, JSON.stringify(loginCreds), {params});
	}

	loginWorker(user, observer) {
		this._workerService.getHumanResourceProfile().subscribe({
			next: (response) => {
				Object.assign(user, user, response);
				user.username = parseInt(user.username, 10);
				this._userService.updateUser(user);
				this.loggedIn$.emit(user.type);
				this._workerService.getAssociatedDrivers().subscribe({
					next: (associatedDrivers) => {
						if (associatedDrivers && associatedDrivers.length > 0) {
							const matchingAssociatedDriver = associatedDrivers.find((ad) => ad.id === user.username);
							user.driverId = matchingAssociatedDriver ? matchingAssociatedDriver.id : associatedDrivers[0].id;
							Object.assign(user, {associatedDrivers: associatedDrivers});
							this._userService.updateUser(user);
						}
						observer.next('work');
						observer.complete();
					}, error: (error) => {
						observer.error(error);
					}
				});
			}, error: (error) => {
				observer.error(error);
			}
		});
	}

	/**
	 * @api {delete} /v2/Authentication Logout
	 * @apiName logout
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	logout() {
		this._alertService.clear();
		localStorage.removeItem('Rapidship:customer');
		return this._http.delete<any>(`${environment.apiUrl}/v2/Authentication`);
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}
	}

	/**
	 * @api {post} /v2/Authentication/IsAlive Check If Token Is Valid
	 * @apiName renewSession
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	renewSession(user) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/IsAlive`, {userId: user.username, token: user.token});
	}

	showAuthModal(error?) {
		this.showAuthModal$.emit(error);
	}

	startSessionTimer() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}

		this.timerSubscription = timer(0, 1000).subscribe(() => {
			if (this.sessionExpiration) {
				const now = new Date();
				const diff = this.sessionExpiration.getTime() - now.getTime();
				const timeoutTimer = diff / 1000;
				const minutes = Math.floor(timeoutTimer / 60);
				const seconds = Math.floor(timeoutTimer - minutes * 60);
				this.idleState$.emit('Your session will timeout in: ' + minutes + ' minutes ' + seconds + ' seconds');

				const warningThreshold = 300; 

				if (timeoutTimer <= warningThreshold) {
					if (!this.bannerCurrentlyShowing) {
						this.bannerCurrentlyShowing = true;
						this.showTimeoutBanner$.emit(true);
					}
				} else if (timeoutTimer > 15 && this.bannerCurrentlyShowing) {
					this.bannerCurrentlyShowing = false;
					this.showTimeoutBanner$.emit(false);
				}

				if (timeoutTimer <= 0) {
					this.authenticationDelete({
						detailedMsg: 'Please enter your login information again to continue using Rapidship.',
						generalMsg: 'You have been logged out.'
					});
				}
			}
		});
	}



	/**
	 * @api {get} /v2/InetUserProfile/{username} Get Inet User Profile and Token by ID
	 * @apiName getInetUserProfile
	 * @apiGroup InetUsers
	 * @apiVersion 20.0.0
	 */
	completeLoginAfterSso(token) {
		return new Observable((observer) => {
			this._http.get<any>(`${environment.apiUrl}/v2/InetUserProfile/${token}`)
				.subscribe({
					next: (response) => {
						//this is going to be whatever user is logged into the SSO provider
						let credentials = {
							username: response.profile.username.toLocaleLowerCase(),
							rememberMe: false
						};
						//compare that to the user that tried to log into Rapidship
						let ssoInfo = JSON.parse(localStorage.getItem('RapidShip:ssoInfo'));
						localStorage.removeItem('RapidShip:ssoInfo'); //also delete this item because we don't need it anymore
						if (credentials.username !== ssoInfo.username) {
							//if it didn't match, set up authentication so we can delete the token that was created on the backend
							this.user = new User();
							this.user.username = credentials.username;
							this.user.token = response.token ? response.token : response;
							localStorage.setItem('Rapidship:user', JSON.stringify(this.user));
							this._http.delete<any>(`${environment.apiUrl}/v2/Authentication`).subscribe({
								next: () => {
									localStorage.removeItem('Rapidship:user'); //we don't need this anymore
									//show the login form with an explanatory error
									this._modalService.displayLoginModal(
										{message: `We are sorry for the inconvenience, but unfortunately your SSO provider does not allow for authentication to our system while more than one SSO user is logged into a session. Please make sure to log out of all sessions for ${ssoInfo.domain} except for your ${ssoInfo.username} account, then please try and log back into our portal with this account.`});
								},
								error: (error) => {
									localStorage.removeItem('Rapidship:user'); //we don't need this anymore
									observer.error(error);
								}
							});
						} else {
							this.loginInitiationSuccess(credentials, 'inet', observer, response);
						}
					},
					error: (err) => {
						observer.error(err);
					}
				});
		});
	}

}
